import React, { useState } from 'react';
import { Shield, Lock, CreditCard, Zap, Check } from 'lucide-react';
type Plan = {
  id: string;
  icon: string;
  name: string;
  contents: string;
  oldPrice: string;
  newPrice: string;
  savings: string;
  accent: string;
  tag?: string;
  cta: string;
};
export function PricingTable() {
  const plans: Plan[] = [{
    id: 'n1',
    icon: '📘',
    name: 'MÉDICO EN PROCESO™ — NIVEL 1',
    contents: '6 Tomos + 6 Bonos',
    oldPrice: '$58.990',
    newPrice: '$19.990',
    savings: '$39.000',
    accent: 'text-slate-900',
    cta: 'EL NIVEL 1'
  }, {
    id: 'n2',
    icon: '⭐',
    name: 'SISTEMA CARRERA ORGANIZADA™ — NIVEL 2',
    contents: '8 Tomos + 6 Bonos · Guardia + Medicina Interna',
    oldPrice: '$102.990',
    newPrice: '$27.990',
    savings: '$75.000',
    accent: 'text-brand-amber',
    tag: '⭐ MÁS ELEGIDO',
    cta: 'EL NIVEL 2'
  }, {
    id: 'n3',
    icon: '🏆',
    name: 'SISTEMA DE GESTIÓN ACADÉMICA™ — NIVEL 3',
    contents: '8 Tomos + 6 Bonos + 7 Editables premium',
    oldPrice: '$149.990',
    newPrice: '$32.990',
    savings: '$117.000',
    accent: 'text-brand-amber',
    tag: '🏆 MÁS COMPLETO',
    cta: 'EL NIVEL 3 COMPLETO'
  }];
  const [selected, setSelected] = useState<string>('n2');
  const selectedPlan = plans.find(p => p.id === selected) ?? plans[1];
  return <section id="pricing-section" className="w-full bg-brand-blue py-16 md:py-24 px-4" data-id="element-385">
      <div className="max-w-2xl mx-auto" data-id="element-386">
        {/* Launch header (copy del spec) */}
        <div className="text-center mb-8 text-white" data-id="element-387">
          <h2 className="text-2xl md:text-4xl font-bold mb-3" data-id="element-388">
            🔥 Precio de lanzamiento — Sube sin previo aviso
          </h2>
          <p className="text-base md:text-lg text-blue-100" data-id="element-389">
            Toda la carrera ordenada. Dejá de improvisar y avanzá de una vez.
          </p>
        </div>

        {/* Tarjeta checkout */}
        <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10" data-id="element-390">
          <div className="text-center mb-8" data-id="element-391">
            <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2" data-id="element-392">
              Elegí tu Nivel:
            </h3>
            <p className="text-base md:text-lg text-slate-500 font-medium" data-id="element-393">
              Las tres opciones incluyen los 6 bonos estratégicos
            </p>
          </div>

          <div className="flex flex-col gap-4 mb-8" data-id="element-394">
            {plans.map(plan => {
            const isSelected = plan.id === selected;
            return <button key={plan.id} type="button" onClick={() => setSelected(plan.id)} aria-pressed={isSelected} className={`w-full text-left rounded-2xl border-2 p-4 md:p-5 transition-all ${isSelected ? 'border-brand-amber bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-395">
                  <div className="flex items-start gap-3 md:gap-4" data-id="element-396">
                    {/* Radio */}
                    <span className={`mt-1 shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-brand-amber' : 'border-slate-300'}`} data-id="element-397">
                      {isSelected && <span className="w-3 h-3 rounded-full bg-brand-amber" data-id="element-398" />}
                    </span>

                    {/* Info */}
                    <div className="flex-grow min-w-0" data-id="element-399">
                      <div className="flex items-center gap-2 font-bold text-slate-900 leading-tight" data-id="element-400">
                        <span className="text-xl" data-id="element-401">{plan.icon}</span>
                        <span className="text-sm md:text-base" data-id="element-402">
                          {plan.name}
                        </span>
                      </div>

                      {plan.tag && <div className="mt-2 flex flex-wrap gap-2" data-id="element-403">
                          <span className="inline-block bg-brand-amber text-white text-[11px] font-bold px-3 py-1 rounded-full" data-id="element-404">
                            {plan.tag}
                          </span>
                          {isSelected && <span className="inline-block bg-amber-100 text-amber-700 text-[11px] font-bold px-3 py-1 rounded-full" data-id="element-405">
                              AHORRÁS {plan.savings}
                            </span>}
                        </div>}

                      <p className="text-xs md:text-sm text-slate-500 mt-2" data-id="element-406">
                        {plan.contents}
                      </p>
                    </div>

                    {/* Precio */}
                    <div className="text-right shrink-0" data-id="element-407">
                      <div className="text-slate-400 line-through text-sm" data-id="element-408">
                        {plan.oldPrice}
                      </div>
                      <div className={`text-2xl md:text-3xl font-extrabold ${plan.accent}`} data-id="element-409">
                        {plan.newPrice}
                      </div>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* CTA dinámico */}
          <button className="w-full bg-brand-amber hover:bg-amber-600 text-white text-lg md:text-xl font-bold py-5 px-6 rounded-2xl shadow-lg transition-transform hover:scale-[1.02] active:scale-95 min-h-[56px] flex items-center justify-center gap-2" data-id="element-410">
            SÍ, QUIERO {selectedPlan.cta} →
          </button>

          <p className="text-center text-sm italic text-slate-400 mt-4" data-id="element-411">
            La opción más elegida por estudiantes de Medicina argentinos
          </p>
        </div>

        {/* Prueba social */}
        <p className="text-center text-base md:text-lg font-medium text-blue-100 mt-10" data-id="element-412">
          +1.800 estudiantes ya tienen toda la carrera de Medicina organizada en
          un solo lugar
        </p>

        {/* Seguridad 2x2 */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 max-w-md mx-auto mt-8 text-sm text-blue-100 font-medium" data-id="element-413">
          <span className="flex items-center gap-2" data-id="element-414">
            <Lock size={16} data-id="element-415" /> Pago seguro
          </span>
          <span className="flex items-center gap-2" data-id="element-416">
            <Zap size={16} data-id="element-417" /> Acceso en 5 min
          </span>
          <span className="flex items-center gap-2" data-id="element-418">
            <Shield size={16} data-id="element-419" /> Garantía 7 días
          </span>
          <span className="flex items-center gap-2" data-id="element-420">
            <CreditCard size={16} data-id="element-421" /> Tarjeta · Transferencia
          </span>
        </div>
      </div>
    </section>;
}